import React from 'react';
import Icon from '../../../components/AppIcon';
import type { PerformanceMetric } from '../types';

const PerformanceSection: React.FC = () => {
  const performanceMetrics: PerformanceMetric[] = [
    {
      id: 'latency',
      metric: 'API Response Time',
      value: '< 100ms',
      description: 'Average API response time for analytics requests',
      icon: 'Zap'
    },
    {
      id: 'throughput',
      metric: 'Processing Throughput',
      value: '1000+ FPS',
      description: 'Frames per second processing capability per server',
      icon: 'Gauge'
    },
    {
      id: 'concurrent',
      metric: 'Concurrent Streams',
      value: '500+',
      description: 'Simultaneous video streams per deployment',
      icon: 'Video'
    },
    {
      id: 'accuracy',
      metric: 'Detection Accuracy',
      value: '98.5%',
      description: 'Average object detection accuracy across all models',
      icon: 'Target'
    },
    {
      id: 'uptime',
      metric: 'System Uptime',
      value: '99.9%',
      description: 'Guaranteed uptime with SLA backing',
      icon: 'Activity'
    },
    {
      id: 'scaling',
      metric: 'Auto-Scaling',
      value: '< 30s',
      description: 'Time to scale resources based on demand',
      icon: 'TrendingUp'
    }
  ];

  const optimizationFeatures = [
    {
      title: 'Code Splitting',
      description: 'Automatic code splitting for faster initial load times',
      icon: 'Code'
    },
    {
      title: 'Lazy Loading',
      description: 'On-demand loading of components and resources',
      icon: 'Download'
    },
    {
      title: 'CDN Distribution',
      description: 'Global CDN for reduced latency worldwide',
      icon: 'Globe'
    },
    {
      title: 'Caching Strategy',
      description: 'Multi-layer caching for optimal performance',
      icon: 'Database'
    },
    {
      title: 'Load Balancing',
      description: 'Intelligent traffic distribution across servers',
      icon: 'Network'
    },
    {
      title: 'Edge Computing',
      description: 'Process data closer to the source for reduced latency',
      icon: 'Cpu'
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Performance Metrics
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Industry-leading performance with optimized infrastructure and intelligent scaling
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {performanceMetrics.map((metric) => (
            <div
              key={metric.id}
              className="bg-card rounded-lg p-6 shadow-brand border border-border hover:border-primary/50 transition-all duration-300"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Icon name={metric.icon} size={28} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-text-secondary mb-1">
                    {metric.metric}
                  </div>
                  <div className="text-3xl font-bold text-primary mb-2">
                    {metric.value}
                  </div>
                </div>
              </div>
              <p className="text-sm text-text-secondary leading-relaxed">
                {metric.description}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg p-8 mb-12">
          <h3 className="text-2xl font-bold text-foreground mb-6 text-center">
            Performance Optimization
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {optimizationFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-card rounded-lg p-6 shadow-brand border border-border"
              >
                <div className="flex items-center gap-3 mb-3">
                  <Icon name={feature.icon} size={24} className="text-primary" />
                  <h4 className="font-semibold text-foreground">{feature.title}</h4>
                </div>
                <p className="text-sm text-text-secondary">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-lg p-8 shadow-brand border border-border">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold text-foreground mb-4">Scalability</h3>
              <p className="text-text-secondary mb-4">
                Our infrastructure automatically scales based on demand, ensuring consistent performance during peak loads while optimizing costs during low-traffic periods.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Icon name="CheckCircle2" size={20} className="text-success mt-0.5" />
                  <div>
                    <div className="font-medium text-foreground">Horizontal Scaling</div>
                    <div className="text-sm text-text-secondary">Add more servers as needed</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Icon name="CheckCircle2" size={20} className="text-success mt-0.5" />
                  <div>
                    <div className="font-medium text-foreground">Vertical Scaling</div>
                    <div className="text-sm text-text-secondary">Increase server resources dynamically</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Icon name="CheckCircle2" size={20} className="text-success mt-0.5" />
                  <div>
                    <div className="font-medium text-foreground">Auto-Scaling</div>
                    <div className="text-sm text-text-secondary">Automatic resource adjustment</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-foreground mb-4">Monitoring & Analytics</h3>
              <p className="text-text-secondary mb-4">
                Real-time monitoring and analytics provide insights into system performance, helping identify and resolve issues before they impact users.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Icon name="CheckCircle2" size={20} className="text-success mt-0.5" />
                  <div>
                    <div className="font-medium text-foreground">Real-time Metrics</div>
                    <div className="text-sm text-text-secondary">Live performance dashboards</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Icon name="CheckCircle2" size={20} className="text-success mt-0.5" />
                  <div>
                    <div className="font-medium text-foreground">Alert System</div>
                    <div className="text-sm text-text-secondary">Proactive issue detection</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Icon name="CheckCircle2" size={20} className="text-success mt-0.5" />
                  <div>
                    <div className="font-medium text-foreground">Performance Reports</div>
                    <div className="text-sm text-text-secondary">Detailed analytics and insights</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PerformanceSection;